import React, { useState, useMemo } from 'react';
import { useAppContext } from '../context/AppContext';
import SafeIcon from '../common/SafeIcon';
import {
  FiUsers, FiCpu, FiGlobe, FiLock, FiTrash2, FiRefreshCcw,
  FiPlus, FiX, FiEdit3, FiSearch, FiShield, FiUser, FiSlash,
  FiCheck, FiArrowLeft, FiFlag, FiClock, FiZap, FiMail, FiCalendar
} from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';

export default function Admin() {
  const {
    currentUser,
    users, blockedUsers, globalGames, globalOptions,
    updateUserStatus, updateUserRole, resetPassword,
    addGlobalGame, updateGlobalGame, deleteGlobalGame,
    addGlobalOption, removeGlobalOption, updateGlobalOption,
    unblockPlayer, blockPlayer
  } = useAppContext();

  const [activeTab, setActiveTab] = useState('users');
  const [gameModal, setGameModal] = useState({ show: false, editMode: false, data: null });
  const [userSearch, setUserSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);

  const filteredUsers = useMemo(() => {
    return users.filter(u =>
      u.id !== currentUser.id &&
      (u.nickname.toLowerCase().includes(userSearch.toLowerCase()) ||
        u.id.toLowerCase().includes(userSearch.toLowerCase()))
    );
  }, [users, userSearch, currentUser.id]);

  if (selectedUser) {
    const liveUser = users.find(u => u.id === selectedUser.id) || selectedUser;
    return (
      <UserDetail
        user={liveUser}
        blockedUsers={blockedUsers}
        onBack={() => setSelectedUser(null)}
        updateUserStatus={updateUserStatus}
        updateUserRole={updateUserRole}
        resetPassword={resetPassword}
        onBlock={blockPlayer}
        onUnblock={unblockPlayer}
      />
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-12">
      <div className="mb-10">
        <h1 className="text-4xl font-black text-brand-950 dark:text-white tracking-tight mb-2 uppercase">Admin Dashboard</h1>
        <p className="text-gray-500 dark:text-dark-muted font-bold tracking-tight">Gestione giocatori e configurazione della piattaforma.</p>
      </div>

      <div className="flex gap-4 mb-8 overflow-x-auto pb-2">
        <TabButton active={activeTab === 'users'} onClick={() => setActiveTab('users')} icon={FiUsers} label="Giocatori" />
        <TabButton active={activeTab === 'games'} onClick={() => setActiveTab('games')} icon={FiCpu} label="Giochi" />
        <TabButton active={activeTab === 'settings'} onClick={() => setActiveTab('settings')} icon={FiGlobe} label="Opzioni" />
      </div>

      <div className="bg-white dark:bg-dark-surface rounded-[40px] shadow-2xl shadow-brand-900/5 border border-white dark:border-dark-border p-8 min-h-[500px]">
        {activeTab === 'users' && (
          <div className="space-y-6">
            <div className="relative max-w-md">
              <SafeIcon icon={FiSearch} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Cerca giocatore per nickname o ID..."
                className="w-full pl-12 pr-4 py-3 bg-gray-50 dark:bg-dark-card border border-gray-100 dark:border-dark-border rounded-2xl font-bold text-sm outline-none focus:ring-2 focus:ring-brand-500/20 transition-all dark:text-white"
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
              />
            </div>
            <UserTable
              users={filteredUsers}
              blockedUsers={blockedUsers}
              updateUserStatus={updateUserStatus}
              updateUserRole={updateUserRole}
              resetPassword={resetPassword}
              onBlock={blockPlayer}
              onUnblock={unblockPlayer}
              onViewDetail={setSelectedUser}
            />
          </div>
        )}

        {activeTab === 'games' && (
          <div className="space-y-8">
            <div className="flex justify-between items-center bg-brand-50 dark:bg-brand-900/10 p-6 rounded-3xl border border-brand-100 dark:border-brand-900/20">
              <div>
                <h3 className="font-black text-brand-950 dark:text-white">Catalogo Giochi</h3>
                <p className="text-xs text-brand-600 dark:text-brand-400 font-bold">Gestisci i titoli disponibili per i giocatori.</p>
              </div>
              <button
                onClick={() => setGameModal({ show: true, editMode: false, data: null })}
                className="bg-brand-600 text-white px-6 py-3 rounded-2xl font-black text-xs uppercase tracking-widest flex items-center gap-2 hover:bg-brand-700 transition-all shadow-lg shadow-brand-200"
              >
                <SafeIcon icon={FiPlus} /> Nuovo Gioco
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(globalGames).map(([name, data]) => (
                <div key={name} className="p-6 bg-white dark:bg-dark-card border border-gray-100 dark:border-dark-border rounded-3xl shadow-sm hover:border-brand-200 transition-all flex justify-between items-start group">
                  <div className="flex-1 min-w-0 mr-4">
                    <h4 className="font-black text-brand-950 dark:text-white text-lg mb-1 truncate">{name}</h4>
                    <p className="text-[10px] font-bold text-gray-400 dark:text-dark-muted uppercase tracking-widest mb-3">{data.roles.length} Ruoli • {data.ranks.length} Rank</p>
                    <div className="flex flex-wrap gap-1.5">
                      {data.roles.slice(0, 2).map(r => <span key={r} className="text-[9px] bg-gray-50 dark:bg-dark-surface px-2 py-0.5 rounded-full font-bold text-gray-500 border border-gray-100 dark:border-dark-border">{r}</span>)}
                      {data.roles.length > 2 && <span className="text-[9px] text-gray-400 font-bold">+{data.roles.length - 2}</span>}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-all">
                    <button onClick={() => setGameModal({ show: true, editMode: true, data: { name, ...data } })} className="p-2 text-brand-400 hover:text-brand-600 hover:bg-brand-50 dark:hover:bg-dark-surface rounded-xl transition-all">
                      <SafeIcon icon={FiEdit3} />
                    </button>
                    <button onClick={() => deleteGlobalGame(name)} className="p-2 text-gray-300 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-all">
                      <SafeIcon icon={FiTrash2} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <OptionManager title="Lingue Supportate" options={globalOptions.languages}
              onAdd={(v) => addGlobalOption('languages', v)}
              onRemove={(v) => removeGlobalOption('languages', v)}
              onUpdate={(oldV, newV) => updateGlobalOption('languages', oldV, newV)}
            />
            <OptionManager title="Piattaforme" options={globalOptions.platforms}
              onAdd={(v) => addGlobalOption('platforms', v)}
              onRemove={(v) => removeGlobalOption('platforms', v)}
              onUpdate={(oldV, newV) => updateGlobalOption('platforms', oldV, newV)}
            />
          </div>
        )}
      </div>

      <AnimatePresence>
        {gameModal.show && (
          <GameModal
            editData={gameModal.data}
            onClose={() => setGameModal({ show: false, editMode: false, data: null })}
            onSave={(name, roles, ranks) => {
              if (gameModal.editMode) updateGlobalGame(gameModal.data.name, name, roles, ranks);
              else addGlobalGame(name, roles, ranks);
              setGameModal({ show: false, editMode: false, data: null });
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── USER DETAIL ─────────────────────────────────────────────────────────────

function UserDetail({ user, blockedUsers, onBack, updateUserStatus, updateUserRole, resetPassword, onBlock, onUnblock }) {
  const isBlocked = blockedUsers.includes(user.id);
  const isAdmin = user.role === 'admin';

  return (
    <motion.div
      initial={{ opacity: 0, x: 40 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 40 }}
      className="max-w-4xl mx-auto px-4 py-12"
    >
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-gray-500 dark:text-dark-muted hover:text-brand-600 dark:hover:text-brand-400 font-black text-xs uppercase tracking-widest mb-8 transition-colors"
      >
        <SafeIcon icon={FiArrowLeft} /> Back to Users
      </button>

      <div className="bg-white dark:bg-dark-surface rounded-[32px] p-8 shadow-xl border border-white dark:border-dark-border mb-6 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-40 h-40 bg-brand-50 dark:bg-brand-900/10 rounded-full -mr-20 -mt-20 blur-3xl opacity-50"></div>
        <div className="flex flex-col sm:flex-row gap-6 items-center sm:items-start relative z-10">
          <div className="relative shrink-0">
            <img src={user.avatar} alt={user.nickname} className="w-28 h-28 rounded-[2rem] object-cover border-4 border-brand-50 dark:border-dark-card shadow-lg" />
            <span className={`absolute -bottom-2 -right-2 w-7 h-7 rounded-full border-4 border-white dark:border-dark-surface ${user.isOnline ? 'bg-green-500' : 'bg-gray-300'}`}></span>
          </div>
          <div className="flex-1 text-center sm:text-left">
            <div className="flex flex-wrap items-center justify-center sm:justify-start gap-3 mb-1">
              <h2 className="text-3xl font-black text-brand-950 dark:text-white tracking-tight">{user.nickname}</h2>
              <span className={`px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest flex items-center gap-1.5 ${isAdmin ? 'bg-amber-100 dark:bg-amber-900/20 text-amber-600 border border-amber-200' : 'bg-blue-50 dark:bg-blue-900/10 text-blue-600 border border-blue-100'}`}>
                <SafeIcon icon={isAdmin ? FiShield : FiUser} className="w-3 h-3" />
                {isAdmin ? 'Admin' : 'Player'}
              </span>
              <StatusBadge status={user.status || 'active'} />
              {isBlocked && <span className="text-[10px] font-black text-red-500 bg-red-50 dark:bg-red-900/20 px-2.5 py-1 rounded-lg border border-red-100">Blocked</span>}
            </div>
            {user.name && <p className="text-gray-400 dark:text-dark-muted font-bold uppercase tracking-widest text-xs mb-4">{user.name}</p>}
            <div className="flex flex-wrap justify-center sm:justify-start gap-2 mt-3">
              <InfoChip icon={FiMail} text={user.email || 'N/A'} />
              <InfoChip icon={FiFlag} text={user.nationality || 'N/A'} />
              <InfoChip icon={FiClock} text={user.timezone || 'N/A'} />
              {user.birthDate && <InfoChip icon={FiCalendar} text={user.birthDate} />}
              {user.gender && <InfoChip icon={FiUser} text={user.gender} />}
            </div>
          </div>
        </div>
        {user.bio && (
          <div className="mt-6 pt-6 border-t border-gray-50 dark:border-dark-border">
            <p className="text-gray-500 dark:text-gray-400 font-medium italic text-sm leading-relaxed">"{user.bio}"</p>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div className="bg-white dark:bg-dark-surface rounded-[24px] p-6 border border-gray-100 dark:border-dark-border shadow-sm">
          <h3 className="text-[10px] font-black text-gray-400 dark:text-dark-muted uppercase tracking-widest mb-4 flex items-center gap-2">
            <SafeIcon icon={FiGlobe} className="text-brand-600" /> Languages
          </h3>
          <div className="flex flex-wrap gap-2">
            {(user.languages || []).map(lang => (
              <span key={lang} className="px-3 py-1.5 bg-brand-50 dark:bg-brand-900/20 text-brand-700 dark:text-brand-300 rounded-xl text-xs font-bold border border-brand-100 dark:border-brand-900/30">{lang}</span>
            ))}
            {(!user.languages || user.languages.length === 0) && <span className="text-xs text-gray-400 font-bold">N/A</span>}
          </div>
        </div>

        <div className="bg-white dark:bg-dark-surface rounded-[24px] p-6 border border-gray-100 dark:border-dark-border shadow-sm">
          <h3 className="text-[10px] font-black text-gray-400 dark:text-dark-muted uppercase tracking-widest mb-4 flex items-center gap-2">
            <SafeIcon icon={FiShield} className="text-brand-600" /> Admin Actions
          </h3>
          <div className="flex flex-wrap gap-2">
            <ActionBtn
              onClick={() => updateUserRole(user.id, isAdmin ? 'player' : 'admin')}
              icon={isAdmin ? FiUser : FiShield}
              label={isAdmin ? 'Remove Admin' : 'Make Admin'}
              variant={isAdmin ? 'gray' : 'brand'}
            />
            <ActionBtn
              onClick={() => isBlocked ? onUnblock(user.id) : onBlock(user.id)}
              icon={isBlocked ? FiCheck : FiSlash}
              label={isBlocked ? 'Unblock' : 'Block'}
              variant={isBlocked ? 'green' : 'red'}
            />
            <ActionBtn
              onClick={() => updateUserStatus(user.id, user.status === 'banned' ? 'active' : 'banned')}
              icon={FiLock}
              label={user.status === 'banned' ? 'Unban' : 'Ban'}
              variant={user.status === 'banned' ? 'green' : 'red'}
            />
            <ActionBtn
              onClick={() => resetPassword(user.id)}
              icon={FiRefreshCcw}
              label="Reset Password"
              variant="gray"
            />
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-dark-surface rounded-[24px] p-6 border border-gray-100 dark:border-dark-border shadow-sm">
        <h3 className="text-[10px] font-black text-gray-400 dark:text-dark-muted uppercase tracking-widest mb-4 flex items-center gap-2">
          <SafeIcon icon={FiCpu} className="text-brand-600" /> Game Library ({(user.games || []).length})
        </h3>
        {(user.games || []).length === 0 ? (
          <p className="text-xs text-gray-400 font-bold">No games registered.</p>
        ) : (
          <div className="grid gap-3">
            {user.games.map((game, idx) => (
              <div key={idx} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-dark-card rounded-2xl border border-gray-100 dark:border-dark-border">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-black text-brand-950 dark:text-white text-sm">{game.name}</span>
                    <span className={`text-[9px] font-black uppercase px-2 py-0.5 rounded-md border ${game.type === 'Competitive' ? 'bg-red-50 text-red-600 border-red-100' : 'bg-emerald-50 text-emerald-600 border-emerald-100'}`}>{game.type}</span>
                    {game.lookingForTeam && (
                      <span className="text-[9px] font-black uppercase px-2 py-0.5 rounded-md bg-amber-50 text-amber-600 border border-amber-100 flex items-center gap-1">
                        <SafeIcon icon={FiZap} className="w-2.5 h-2.5" /> LFG
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {(game.roles || []).map(r => (
                      <span key={r} className="text-[9px] bg-white dark:bg-dark-surface text-gray-500 px-2 py-0.5 rounded-lg font-bold border border-gray-100 dark:border-dark-border">{r}</span>
                    ))}
                  </div>
                </div>
                <div className="text-right shrink-0 ml-4">
                  <span className="block text-[9px] font-black text-gray-400 uppercase tracking-widest mb-1">Rank</span>
                  <span className="font-black text-brand-600 dark:text-brand-400 bg-brand-50 dark:bg-brand-900/20 px-3 py-1.5 rounded-xl border border-brand-100 dark:border-brand-900/30 text-xs">{game.rank || '-'}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
}

function InfoChip({ icon, text }) {
  return (
    <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-50 dark:bg-dark-card rounded-xl border border-gray-100 dark:border-dark-border text-[11px] font-bold text-gray-600 dark:text-gray-400">
      <SafeIcon icon={icon} className="w-3 h-3 text-gray-400" />
      {text}
    </div>
  );
}

function ActionBtn({ onClick, icon, label, variant = 'gray' }) {
  const styles = {
    gray: 'bg-gray-50 dark:bg-dark-card text-gray-600 dark:text-gray-400 border-gray-100 dark:border-dark-border hover:border-gray-300',
    red: 'bg-red-50 dark:bg-red-900/10 text-red-500 border-red-100 dark:border-red-900/20 hover:bg-red-100',
    green: 'bg-emerald-50 dark:bg-emerald-900/10 text-emerald-600 border-emerald-100 dark:border-emerald-900/20 hover:bg-emerald-100',
    brand: 'bg-brand-50 dark:bg-brand-900/10 text-brand-600 dark:text-brand-400 border-brand-100 dark:border-brand-900/20 hover:bg-brand-100',
  };
  return (
    <button onClick={onClick} className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border font-black text-[10px] uppercase tracking-widest transition-all ${styles[variant]}`}>
      <SafeIcon icon={icon} className="w-3.5 h-3.5" />
      {label}
    </button>
  );
}

// ─── USER TABLE ───────────────────────────────────────────────────────────────

function UserTable({ users, blockedUsers, updateUserStatus, updateUserRole, resetPassword, onBlock, onUnblock, onViewDetail }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left">
        <thead>
          <tr className="border-b border-gray-50 dark:border-dark-border">
            <th className="pb-4 text-[10px] font-black text-gray-400 uppercase tracking-widest px-4">Giocatore</th>
            <th className="pb-4 text-[10px] font-black text-gray-400 uppercase tracking-widest px-4">Ruolo</th>
            <th className="pb-4 text-[10px] font-black text-gray-400 uppercase tracking-widest px-4">Stato</th>
            <th className="pb-4 text-[10px] font-black text-gray-400 uppercase tracking-widest px-4 text-right">Azioni</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50 dark:divide-dark-border">
          {users.map(user => {
            const role = user.role === 'admin' ? 'admin' : 'player';
            const isBlocked = blockedUsers.includes(user.id);
            return (
              <tr key={user.id} className="group hover:bg-gray-50/50 dark:hover:bg-dark-card/50 transition-colors">
                <td className="py-4 px-4 whitespace-nowrap">
                  <div className="flex items-center gap-3 cursor-pointer" onClick={() => onViewDetail(user)}>
                    <img src={user.avatar} className="w-10 h-10 rounded-xl object-cover shadow-sm" alt={user.nickname} />
                    <div>
                      <p className="font-black text-brand-950 dark:text-white text-sm group-hover:text-brand-600 transition-colors">{user.nickname}</p>
                      <p className="text-[10px] text-gray-400 font-bold">{user.id}</p>
                    </div>
                  </div>
                </td>
                <td className="py-4 px-4">
                  <span className={`text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-lg border inline-flex items-center gap-1 ${role === 'admin' ? 'bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400 border-orange-100 dark:border-orange-900/30' : 'bg-blue-50 dark:bg-blue-900/10 text-blue-600 dark:text-blue-400 border-blue-100 dark:border-blue-900/20'}`}>
                    {role}
                  </span>
                </td>
                <td className="py-4 px-4">
                  <div className="flex flex-col gap-1">
                    <StatusBadge status={user.status || 'active'} />
                    {isBlocked && <span className="text-[8px] font-black text-red-500 uppercase tracking-widest">Blocked by Admin</span>}
                  </div>
                </td>
                <td className="py-4 px-4">
                  <div className="flex gap-2 justify-end">
                    <button
                      onClick={() => onViewDetail(user)}
                      className="flex items-center gap-1.5 p-2 rounded-xl transition-all font-bold text-[9px] uppercase tracking-tighter text-brand-400 hover:bg-brand-50 dark:hover:bg-brand-900/10"
                    >
                      <SafeIcon icon={FiUser} className="w-3.5 h-3.5" /> Dettaglio
                    </button>
                    <ActionButton onClick={() => updateUserRole(user.id, role === 'admin' ? 'player' : 'admin')} icon={role === 'admin' ? FiUser : FiShield} label={role === 'admin' ? '- Admin' : '+ Admin'} variant={role === 'admin' ? 'gray' : 'brand'} />
                    <ActionButton onClick={() => isBlocked ? onUnblock(user.id) : onBlock(user.id)} icon={isBlocked ? FiCheck : FiSlash} label={isBlocked ? 'Unblock' : 'Block'} variant={isBlocked ? 'green' : 'red'} />
                    <ActionButton onClick={() => updateUserStatus(user.id, user.status === 'banned' ? 'active' : 'banned')} icon={FiLock} label={user.status === 'banned' ? 'Sblocca' : 'Ban'} variant={user.status === 'banned' ? 'green' : 'red'} />
                    <ActionButton onClick={() => resetPassword(user.id)} icon={FiRefreshCcw} label="Pass" />
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─── OPTION MANAGER ───────────────────────────────────────────────────────────

function OptionManager({ title, options, onAdd, onRemove, onUpdate }) {
  const [val, setVal] = useState('');
  const [editingIndex, setEditingIndex] = useState(null);
  const [editVal, setEditVal] = useState('');

  const handleUpdate = (oldVal) => {
    if (editVal.trim() && editVal !== oldVal) onUpdate(oldVal, editVal.trim());
    setEditingIndex(null);
  };

  return (
    <div className="p-6 bg-gray-50/50 dark:bg-dark-card/50 rounded-3xl border border-gray-100 dark:border-dark-border">
      <h3 className="text-[10px] font-black text-gray-400 dark:text-dark-muted uppercase tracking-widest mb-4">{title}</h3>
      <div className="flex flex-wrap gap-2 mb-6">
        {options.map((opt, idx) => (
          <div key={opt} className="group relative">
            {editingIndex === idx ? (
              <div className="flex items-center gap-1 bg-white dark:bg-dark-surface border border-brand-200 rounded-xl p-1 shadow-sm">
                <input autoFocus type="text" value={editVal} onChange={(e) => setEditVal(e.target.value)}
                  onBlur={() => handleUpdate(opt)} onKeyDown={(e) => e.key === 'Enter' && handleUpdate(opt)}
                  className="bg-transparent border-none outline-none px-2 py-0.5 text-sm font-bold w-24 dark:text-white" />
              </div>
            ) : (
              <div className="flex items-center gap-1 bg-white dark:bg-dark-surface px-3 py-1.5 rounded-xl border border-gray-100 dark:border-dark-border text-sm font-bold text-gray-700 dark:text-gray-300 shadow-sm transition-all hover:border-brand-200">
                <span>{opt}</span>
                <div className="flex items-center ml-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => { setEditingIndex(idx); setEditVal(opt); }} className="p-1 text-gray-400 hover:text-brand-500">
                    <SafeIcon icon={FiEdit3} className="w-3 h-3" />
                  </button>
                  <button onClick={() => onRemove(opt)} className="p-1 text-gray-400 hover:text-red-500">
                    <SafeIcon icon={FiTrash2} className="w-3 h-3" />
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <input type="text" value={val} onChange={(e) => setVal(e.target.value)} placeholder="Aggiungi..."
          className="flex-1 bg-white dark:bg-dark-surface border border-gray-100 dark:border-dark-border rounded-xl px-4 py-2 text-sm font-bold outline-none focus:ring-2 focus:ring-brand-500/20 dark:text-white" />
        <button onClick={() => { if (val) { onAdd(val); setVal(''); } }}
          className="bg-brand-600 text-white p-2.5 rounded-xl shadow-lg shadow-brand-100 hover:bg-brand-700 transition-all">
          <SafeIcon icon={FiPlus} />
        </button>
      </div>
    </div>
  );
}

// ─── GAME MODAL ───────────────────────────────────────────────────────────────

function GameModal({ editData, onClose, onSave }) {
  const [name, setName] = useState(editData?.name || '');
  const [roles, setRoles] = useState(editData?.roles.join(',') || '');
  const [ranks, setRanks] = useState(editData?.ranks.join(',') || '');
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} className="absolute inset-0 bg-brand-950/40 backdrop-blur-sm" />
      <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
        className="relative bg-white dark:bg-dark-surface w-full max-w-md rounded-[32px] p-8 shadow-2xl border border-white/20">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-2xl font-black text-brand-950 dark:text-white uppercase tracking-tight">{editData ? 'Modifica Gioco' : 'Crea Gioco'}</h3>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600"><SafeIcon icon={FiX} /></button>
        </div>
        <div className="space-y-6">
          <AdminInput label="Nome Gioco" value={name} onChange={setName} placeholder="es. Fortnite" />
          <AdminInput label="Ruoli (separati da virgola)" value={roles} onChange={setRoles} placeholder="es. Builder,Sniper" />
          <AdminInput label="Rank (separati da virgola)" value={ranks} onChange={setRanks} placeholder="es. Bronze,Silver" />
          <button
            onClick={() => onSave(name, roles.split(',').map(s => s.trim()).filter(s => s), ranks.split(',').map(s => s.trim()).filter(s => s))}
            disabled={!name || !roles || !ranks}
            className="w-full py-4 bg-brand-600 text-white rounded-2xl font-black text-xs uppercase tracking-widest shadow-lg shadow-brand-200 disabled:opacity-50"
          >
            {editData ? 'Salva Modifiche' : 'Aggiungi Gioco'}
          </button>
        </div>
      </motion.div>
    </div>
  );
}

function AdminInput({ label, value, onChange, placeholder }) {
  return (
    <div className="space-y-2">
      <label className="block text-[10px] font-black text-gray-400 dark:text-dark-muted uppercase tracking-widest px-1">{label}</label>
      <input type="text" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className="w-full bg-gray-50 dark:bg-dark-card border border-gray-100 dark:border-dark-border rounded-2xl p-4 font-bold text-gray-700 dark:text-gray-200 outline-none focus:ring-2 focus:ring-brand-500/20" />
    </div>
  );
}

function TabButton({ active, onClick, icon, label }) {
  return (
    <button onClick={onClick} className={`flex items-center gap-3 px-6 py-4 rounded-2xl font-black text-xs uppercase tracking-widest transition-all ${active ? 'bg-brand-600 text-white shadow-xl shadow-brand-200 scale-105' : 'bg-white dark:bg-dark-card text-gray-400 dark:text-dark-muted hover:bg-gray-50 dark:hover:bg-dark-surface border border-gray-100 dark:border-dark-border'}`}>
      <SafeIcon icon={icon} /> {label}
    </button>
  );
}

function StatusBadge({ status }) {
  const configs = {
    active: { text: 'Attivo', classes: 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 border-green-100 dark:border-green-900/30' },
    banned: { text: 'Bannato', classes: 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 border-red-100 dark:border-red-900/30' },
    shadowbanned: { text: 'Shadowban', classes: 'bg-gray-100 dark:bg-dark-border text-gray-500 dark:text-dark-muted border-gray-200 dark:border-dark-border' }
  };
  const config = configs[status] || configs.active;
  return <span className={`text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-lg border ${config.classes}`}>{config.text}</span>;
}

function ActionButton({ onClick, icon, label, variant = 'gray' }) {
  const styles = {
    gray: 'text-gray-400 hover:bg-gray-100 dark:hover:bg-dark-card',
    red: 'text-red-400 hover:bg-red-50 dark:hover:bg-red-900/10',
    green: 'text-green-400 hover:bg-green-50 dark:hover:bg-green-900/10',
    brand: 'text-brand-600 hover:bg-brand-50 dark:hover:bg-brand-900/10'
  };
  return (
    <button onClick={onClick} className={`flex items-center gap-1.5 p-2 rounded-xl transition-all font-bold text-[9px] uppercase tracking-tighter ${styles[variant]}`}>
      <SafeIcon icon={icon} className="w-3.5 h-3.5" /> {label}
    </button>
  );
}